import { useState } from 'react';

interface InterestCategory {
  label: string;
  icon: string;
  items: string[];
}

const interestCategories: InterestCategory[] = [
  {
    label: 'Müzik',
    icon: 'ri-music-2-line',
    items: ['Pop', 'Rock', 'Hip-Hop', 'Elektronik', 'Klasik', 'Jazz', 'R&B', 'Metal', 'Indie', 'K-Pop'],
  },
  {
    label: 'Spor & Fitness',
    icon: 'ri-run-line',
    items: ['Futbol', 'Basketbol', 'Yüzme', 'Koşu', 'Yoga', 'Fitness', 'Dövüş Sporları', 'Tenis', 'Voleybol', 'E-Spor'],
  },
  {
    label: 'Sanat & Kültür',
    icon: 'ri-palette-line',
    items: ['Fotoğrafçılık', 'Resim', 'Heykel', 'Tasarım', 'Sinema', 'Tiyatro', 'Anime', 'Manga', 'Karikatür'],
  },
  {
    label: 'Teknoloji',
    icon: 'ri-code-line',
    items: ['Programlama', 'Oyun', 'Yapay Zeka', 'Kripto', 'Siber Güvenlik', 'Donanım', 'Mobil', 'Web'],
  },
  {
    label: 'Yaşam Tarzı',
    icon: 'ri-heart-3-line',
    items: ['Seyahat', 'Yemek', 'Moda', 'Doğa', 'Hayvanseverlik', 'Kitap', 'Podcast', 'Minimalizm'],
  },
  {
    label: 'Eğitim',
    icon: 'ri-graduation-cap-line',
    items: ['Dil Öğrenimi', 'Tarih', 'Bilim', 'Felsefe', 'Psikoloji', 'Ekonomi', 'Hukuk'],
  },
];

const InterestsSection = () => {
  const [selected, setSelected] = useState<string[]>([
    'Müzik', 'Seyahat', 'Programlama', 'Oyun', 'Yemek',
  ]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const MAX = 15;

  const toggle = (item: string) => {
    setSelected((prev) => {
      if (prev.includes(item)) return prev.filter((x) => x !== item);
      if (prev.length >= MAX) return prev;
      return [...prev, item];
    });
  };

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => { setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2500); }, 900);
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-xl font-extrabold text-white mb-1">İlgi Alanları</h2>
        <p className="text-white/40 text-sm">
          Seçtiğin ilgi alanları eşleşme algoritmamıza yön verir.{' '}
          <span className="text-cyan-400 font-semibold">{selected.length}/{MAX}</span> seçildi.
        </p>
      </div>

      {/* Selected tags preview */}
      {selected.length > 0 && (
        <div className="p-4 rounded-2xl bg-cyan-500/5 border border-cyan-500/15 mb-6">
          <p className="text-white/35 text-xs mb-3 font-semibold uppercase tracking-wide">Seçilen İlgi Alanlarım</p>
          <div className="flex flex-wrap gap-2">
            {selected.map((tag) => (
              <button
                key={tag}
                onClick={() => toggle(tag)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/20 border border-cyan-500/35 text-cyan-300 text-xs font-medium hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400 transition-all cursor-pointer group whitespace-nowrap"
              >
                {tag}
                <i className="ri-close-line text-xs opacity-60 group-hover:opacity-100" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category sections */}
      <div className="flex flex-col gap-6">
        {interestCategories.map((cat) => (
          <div key={cat.label} className="p-6 rounded-2xl bg-white/3 border border-white/8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/8 text-white/50">
                <i className={`${cat.icon} text-base`} />
              </div>
              <span className="text-white/70 font-semibold text-sm">{cat.label}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {cat.items.map((item) => {
                const isSelected = selected.includes(item);
                const isDisabled = !isSelected && selected.length >= MAX;
                return (
                  <button
                    key={item}
                    onClick={() => !isDisabled && toggle(item)}
                    className={`px-3.5 py-2 rounded-full border text-xs font-medium transition-all whitespace-nowrap ${
                      isSelected
                        ? 'bg-cyan-500/18 border-cyan-500/40 text-cyan-300 cursor-pointer hover:bg-red-500/15 hover:border-red-500/25 hover:text-red-400'
                        : isDisabled
                        ? 'bg-white/2 border-white/6 text-white/20 cursor-not-allowed'
                        : 'bg-white/4 border-white/12 text-white/50 hover:bg-white/8 hover:text-white/75 cursor-pointer'
                    }`}
                  >
                    {isSelected && <i className="ri-check-line mr-1 text-xs" />}
                    {item}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Limit warning */}
      {selected.length >= MAX && (
        <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-yellow-500/8 border border-yellow-500/20 text-yellow-400 text-xs">
          <i className="ri-alert-line" />
          Maksimum {MAX} ilgi alanı seçebilirsin. Yeni eklemek için önce birini kaldır.
        </div>
      )}

      {/* Save */}
      <div className="mt-6">
        <button
          onClick={handleSave}
          disabled={saving || selected.length === 0}
          className="px-8 py-3.5 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-bold text-sm hover:from-cyan-400 hover:to-violet-500 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap shadow-lg shadow-cyan-500/20"
        >
          {saving ? (
            <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin" /> Kaydediliyor...</span>
          ) : saved ? (
            <span className="flex items-center gap-2 text-emerald-300"><i className="ri-checkbox-circle-line" /> Kaydedildi!</span>
          ) : (
            <span className="flex items-center gap-2"><i className="ri-save-line" /> İlgi Alanlarını Kaydet</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default InterestsSection;
